import React, { useCallback, useMemo, useState } from 'react';

import { gql, useQuery } from '@apollo/client';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ClickAwayListener, MenuItem, MenuList, Popover } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'wouter';

import { useProfileCriteria } from '../../contexts/ProfileImgContext';
import WHO_AM_I_QUERY from '../../contexts/WhoAmI.graphql';

const SideFooter = ({ collapsed }) => {
  const { profileCriteria } = useProfileCriteria();
  const { data: personalInfo } = useQuery(gql(WHO_AM_I_QUERY), {
    variables: profileCriteria,
  });
  const [, setLocation] = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes } = useStyles({ collapsed });

  const avatarImgUrl = useMemo(() => {
    const prefixWithBucket = (value) =>
      process.env.S3_BUCKET_URL ? process.env.S3_BUCKET_URL + value : value;

    if (profileCriteria) {
      return prefixWithBucket(profileCriteria);
    }
    const profilePictureUrl = personalInfo?.me?.profileImage?.profilePictureUrl;
    return profilePictureUrl ? prefixWithBucket(profilePictureUrl) : '';
  }, [personalInfo, profileCriteria]);

  const handleProfile = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleNavigate = useCallback(
    (path) => {
      setLocation(path);
    },
    [setLocation],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);

  return (
    <Box className={classes.profile}>
      {!collapsed && (
        <>
          <Avatar src={avatarImgUrl} className={classes.avatar} />
          <Typography className={classes.profileName}>
            {`${personalInfo?.me?.firstName} ${personalInfo?.me?.lastName}`}
          </Typography>
        </>
      )}
      <Tooltip title="Profile menu">
        <IconButton
          aria-label="Profile menu"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? 'profile-menu' : undefined}
          onClick={handleProfile}
          className={classes.iconButton}
        >
          {collapsed ? (
            <Avatar src={avatarImgUrl} className={classes.avatar} />
          ) : (
            <MoreVertIcon />
          )}
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        onClose={handleClose}
        id="profile-popover"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Paper className={classes.profileMenu}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList id="profile-menu" autoFocusItem={false}>
              <MenuItem
                className={classes.menuItem}
                onClick={() => handleNavigate('/settings')}
              >
                View Profile
              </MenuItem>
              <MenuItem
                className={classes.menuItem}
                onClick={() => handleNavigate('/logout')}
              >
                Logout
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popover>
    </Box>
  );
};

export default SideFooter;

const useStyles = makeStyles()((theme, { collapsed }) => ({
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(collapsed ? 0 : 1),
    padding: '12px 10px',
    justifyContent: collapsed ? 'center' : 'space-between',
    borderTop: '1px solid #E5E7EB',
  },
  iconButton: {
    width: 44,
    height: 44,
    flexShrink: 0,
  },
  avatar: {
    height: 35,
    width: 35,
    fontSize: '1rem',
  },
  profileName: {
    fontSize: 15,
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  profileMenu: {
    boxShadow: theme.shadows[3],
    width: 200,
    [theme.breakpoints.up('xl')]: {
      width: 300,
    },
  },
  menuItem: {
    fontSize: 14,
    '&:hover': { backgroundColor: '#EDEFF9' },
  },
}));
